import React from "react";
import { useNavigate } from "react-router";
import { useUserUiPreferences } from "../hooks/useUserUiPreferences.js";
import { dismissProductUpdateAnnouncement, fetchProductUpdateAnnouncement } from "../lib/productUpdatesApi.js";
import { subscribePlanningLiveSyncActivity } from "../utils/planningMutationActivity.js";
import { canOpenProductUpdateAnnouncement, PRODUCT_UPDATE_GATE_MS, publicationAnnouncementId, publicationDismissalId, startProductUpdatePolling } from "../utils/productUpdateAnnouncementGate.js";
import ProductUpdateAnnouncementDialog from "./ProductUpdateAnnouncementDialog.jsx";
import { mergeAnnouncementContents } from "../utils/productUpdateAnnouncementPages.js";

export default function ProductUpdateAnnouncement({ optimizationStatus }) {
  const navigate = useNavigate();
  const { flush, reload, dismissedProductUpdateIds } = useUserUiPreferences();
  const [publication, setPublication] = React.useState(null);
  const [page, setPage] = React.useState(0);
  const [error, setError] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const dialogRef = React.useRef(null);
  const titleRef = React.useRef(null);
  const previousFocus = React.useRef(null);
  const dismissedHere = React.useRef(new Set());
  const pendingPublication = React.useRef(null);
  const dismissalInFlight = React.useRef(false);
  const latest = React.useRef({});
  latest.current = { publication, optimizationStatus, dismissedProductUpdateIds, saving };

  const closeDialog = React.useCallback(() => {
    if (dialogRef.current?.open) {
      dialogRef.current.close();
      if (previousFocus.current?.isConnected) previousFocus.current.focus();
      previousFocus.current = null;
    }
  }, []);

  const isDismissed = React.useCallback(id => dismissedHere.current.has(id)
    || latest.current.dismissedProductUpdateIds.includes(publicationDismissalId(id)), []);

  // Every publication not yet closed is shown in the same window, after the previous ones.
  const receivePublication = React.useCallback(next => {
      const unseen = next && !next.dismissed ? (next.publications || [next]).filter(item => !isDismissed(publicationAnnouncementId(item))) : [];
      if (!unseen.length) {
        setPublication(null);
        return;
      }
      const ids = unseen.map(publicationAnnouncementId);
      if (latest.current.publication?.ids.join() !== ids.join()
        || latest.current.publication?.references.map(item => item.content_hash).join() !== unseen.map(item => item.content_hash).join()) {
        setPage(0);
        setPublication({ ids, references: unseen.map(item => ({ publication_id: item.id,
          announcement_id: publicationAnnouncementId(item), content_hash: item.content_hash, published_at: item.published_at })),
          content_json: mergeAnnouncementContents(unseen.map(item => item.content_json)) });
      }
  }, [isDismissed]);

  React.useEffect(() => startProductUpdatePolling({
    fetchAnnouncement: fetchProductUpdateAnnouncement,
    onPublication: next => {
      if (dialogRef.current?.open) {
        pendingPublication.current = next;
        return;
      }
      pendingPublication.current = null;
      receivePublication(next);
    },
  }), [receivePublication]);

  React.useEffect(() => {
    function reconsider() {
      const state = latest.current;
      const current = state.publication;
      const dialog = dialogRef.current;
      // Once visible, the reader owns the window. Polling and preferences from
      // another tab can only affect a future opening, never close this one.
      if (dialog?.open) return;
      if (!current || current.ids.every(isDismissed)) return;
      if (!dialog?.open && canOpenProductUpdateAnnouncement({ optimizationStatus: state.optimizationStatus, dialog })) {
        previousFocus.current = document.activeElement;
        dialog.showModal();
        titleRef.current?.focus();
      }
    }
    reconsider();
    const interval = setInterval(reconsider, PRODUCT_UPDATE_GATE_MS);
    const unsubscribe = subscribePlanningLiveSyncActivity(reconsider);
    return () => { clearInterval(interval); unsubscribe(); };
  }, [publication, optimizationStatus, dismissedProductUpdateIds, saving, closeDialog, isDismissed]);
  React.useEffect(() => () => closeDialog(), [closeDialog]);

  async function dismiss(closeMethod = "understood") {
    if (!publication || dismissalInFlight.current) return;
    const { ids } = publication;
    dismissalInFlight.current = true;
    setSaving(true);
    setError("");
    try {
      // Finish earlier preference saves before the server atomically saves the receipt and closures.
      await flush();
      await dismissProductUpdateAnnouncement(publication.references, closeMethod);
      ids.forEach(id => dismissedHere.current.add(id));
      closeDialog();
      setPublication(null);
      const next = pendingPublication.current;
      pendingPublication.current = null;
      if (next) receivePublication(next);
      if (closeMethod === "journal") navigate("/updates");
      // The closure is already durable; cache refresh failure must not reopen it.
      void reload().catch(() => {});
    } catch {
      setError("La fermeture n'a pas pu être enregistrée. Réessayez.");
    } finally { dismissalInFlight.current = false; setSaving(false); }
  }

  return <ProductUpdateAnnouncementDialog content={publication?.content_json} dialogRef={dialogRef} titleRef={titleRef}
    page={page} onPageChange={setPage} saving={saving} error={error}
    onDismiss={dismiss} onViewJournal={() => dismiss("journal")} />;
}
